import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithCustomToken,
  signOut,
  type User,
} from "firebase/auth";
import { firebaseAuth } from "./firebase";

type AuthState = {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  signInWithToken: (customToken: string) => Promise<void>;
  signOutNow: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  refreshClaims: () => Promise<void>;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  // If Firebase couldn't initialize (SSR / missing config) treat as
  // "not loading, anonymous". Marketing pages don't gate on this.
  const [loading, setLoading] = useState<boolean>(!!firebaseAuth);

  useEffect(() => {
    if (!firebaseAuth) {
      setLoading(false);
      return;
    }
    const unsubAuth = onAuthStateChanged(firebaseAuth, (u) => {
      setUser(u);
      setLoading(false);
    });
    const unsubToken = onIdTokenChanged(firebaseAuth, async (u) => {
      if (!u) {
        setIsAdmin(false);
        return;
      }
      try {
        const result = await u.getIdTokenResult();
        setIsAdmin(result.claims?.admin === true);
      } catch {
        setIsAdmin(false);
      }
    });
    return () => {
      unsubAuth();
      unsubToken();
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      isAdmin,
      signInWithToken: async (customToken: string) => {
        if (!firebaseAuth) throw new Error("Firebase Auth ist nicht konfiguriert.");
        await signInWithCustomToken(firebaseAuth, customToken);
        // Force fresh ID token so the new claims are present immediately.
        if (firebaseAuth.currentUser) {
          const result = await firebaseAuth.currentUser.getIdTokenResult(true);
          setIsAdmin(result.claims?.admin === true);
        }
      },
      signOutNow: async () => {
        if (!firebaseAuth) return;
        await signOut(firebaseAuth);
        setIsAdmin(false);
      },
      getIdToken: async () => {
        if (!firebaseAuth?.currentUser) return null;
        return firebaseAuth.currentUser.getIdToken();
      },
      refreshClaims: async () => {
        if (!firebaseAuth?.currentUser) return;
        const result = await firebaseAuth.currentUser.getIdTokenResult(true);
        setIsAdmin(result.claims?.admin === true);
      },
    }),
    [user, loading, isAdmin]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
